import API_BASE_URL from './apiBaseUrl';
import { tokenRefreshService } from './tokenRefresh';
import { getToken, logout } from './auth';

/**
 * Unified API Client
 * Centralizes all API calls with automatic 401 handling and token refresh
 * 
 * Features:
 * - Automatic token refresh before requests
 * - Unified 401 error handling
 * - Automatic redirect to login on auth failure
 * - Request/response interceptors
 * - Consistent error handling
 */

class ApiClient {
	constructor() {
		this.baseURL = API_BASE_URL;
		this.isRefreshing = false;
		this.failedQueue = [];

		// Configuration
		this.autoHandleAuth = true;
		this.redirectOn401 = true;
		this.maxRetries = 1;

		// Retry configuration for transient server errors (5xx)
		this.maxServerErrorRetries = 2;
		this.serverErrorRetryDelay = 2000; // ms
	}

	/**
	 * Add auth headers to request
	 */
	addAuthHeaders(headers = {}) {
		const token = getToken();
		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}
		return headers;
	}

	/**
	 * Process failed request queue after token refresh
	 */
	processQueue(error, token = null) {
		this.failedQueue.forEach(({ resolve, reject }) => {
			if (error) {
				reject(error);
			} else {
				resolve(token);
			}
		});
		
		this.failedQueue = [];
	}

	/**
	 * Handle 401 Unauthorized responses
	 */
	async handle401(response, originalRequest) {
		console.log('Handling 401 response...');

		// If we're already refreshing, queue this request
		if (this.isRefreshing) {
			return new Promise((resolve, reject) => {
				this.failedQueue.push({ resolve, reject });
			}).then(() => {
				// Retry the original request with new token
				return this.retryRequest(originalRequest);
			});
		}

		this.isRefreshing = true;

		try {
			// Attempt to refresh the token
			const newToken = await tokenRefreshService.refreshToken();
			
			if (newToken) {
				// Token refreshed successfully
				this.processQueue(null, newToken);
				this.isRefreshing = false;
				
				// Retry the original request with new token
				return this.retryRequest(originalRequest);
			} else {
				// Refresh failed - token service will handle redirect
				const error = new Error('Authentication failed');
				error.status = 401;
				this.processQueue(error);
				this.isRefreshing = false;
				throw error;
			}
		} catch (error) {
			// Refresh failed
			this.processQueue(error);
			this.isRefreshing = false;
			
			// The tokenRefreshService will handle the redirect
			// Just throw the error to stop the request chain
			throw error;
		}
	}

	/**
	 * Retry a request with updated token
	 */
	async retryRequest(originalRequest) {
		const { url, options } = originalRequest;

		// Update auth headers with new token
		options.headers = this.addAuthHeaders(options.headers || {});

		// Make the retry request
		const response = await fetch(url, options);

		// If retry also gets 401, the session is truly invalid — force logout
		if (response.status === 401) {
			console.log('Retry request still unauthorized after token refresh, logging out');
			logout({ returnHere: true });
			const error = new Error('Authentication failed');
			error.status = 401;
			throw error;
		}

		if (!response.ok) {
			// The same error shape as a first-try refusal (status, data,
			// message), so callers show the API's reason either way.
			throw await this.buildHttpError(response);
		}

		return response;
	}

	/**
	 * An Error for a non-OK response: status, the parsed body as data, and the
	 * API's detail or message as message (truncated to 500 characters).
	 */
	async buildHttpError(response) {
		const error = new Error(`API request failed: ${response.status} ${response.statusText}`);
		error.status = response.status;
		error.response = response;
		try {
			const errorData = await response.json();
			error.message = errorData.detail || errorData.message || error.message;
			if (error.message.length > 500) {
				error.message = error.message.substring(0, 500) + '...';
			}
			error.data = errorData;
		} catch {
			// Response wasn't JSON, keep original error message
		}
		return error;
	}

	/**
	 * Main request method with unified error handling
	 */
	async request(url, options = {}, _retryCount = 0) {
		// Ensure full URL
		const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;

		// Check if auth should be skipped for this request
		const skipAuth = options.skipAuth || false;
		delete options.skipAuth; // Remove from options before fetch

		// A request that does real work on every attempt (a report pull, an
		// export) must not be re-run on a 5xx: pass retryServerErrors: false.
		const retryServerErrors = options.retryServerErrors !== false;
		delete options.retryServerErrors;

		// Add auth headers (unless auth is skipped)
		if (this.autoHandleAuth && !skipAuth) {
			options.headers = this.addAuthHeaders(options.headers || {});
		}

		// Ensure content-type
		if (options.body && typeof options.body === 'string') {
			options.headers = options.headers || {};
			if (!options.headers['Content-Type']) {
				options.headers['Content-Type'] = 'application/json';
			}
		}

		try {
			// Make the request
			const response = await fetch(fullUrl, options);

			// Handle 401 Unauthorized
			if (response.status === 401 && this.autoHandleAuth && !skipAuth) {
				// Only attempt token refresh for authenticated endpoints (not skipAuth endpoints)
				return this.handle401(response, { url: fullUrl, options });
			}

			// Retry on 5xx server errors (transient failures, e.g. during deployments)
			if (retryServerErrors && response.status >= 500 && _retryCount < this.maxServerErrorRetries) {
				console.log(`Server error ${response.status} on ${options.method || 'GET'} ${url}, retrying (${_retryCount + 1}/${this.maxServerErrorRetries})...`);
				await new Promise(resolve => setTimeout(resolve, this.serverErrorRetryDelay));
				return this.request(url, { ...options, skipAuth, retryServerErrors }, _retryCount + 1);
			}

			// Handle other HTTP errors
			if (!response.ok) {
				throw await this.buildHttpError(response);
			}

			return response;
		} catch (error) {
			// Network errors or other fetch failures — also retry
			if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
				if (retryServerErrors && _retryCount < this.maxServerErrorRetries) {
					console.log(`Network error on ${options.method || 'GET'} ${url}, retrying (${_retryCount + 1}/${this.maxServerErrorRetries})...`);
					await new Promise(resolve => setTimeout(resolve, this.serverErrorRetryDelay));
					return this.request(url, { ...options, skipAuth, retryServerErrors }, _retryCount + 1);
				}

				const networkError = new Error('Network request failed. Please check your connection.');
				networkError.isNetworkError = true;
				throw networkError;
			}

			throw error;
		}
	}

	/**
	 * Convenience methods for different HTTP verbs
	 */
	async get(url, options = {}) {
		return this.request(url, { ...options, method: 'GET' });
	}

	async post(url, body, options = {}) {
		return this.request(url, {
			...options,
			method: 'POST',
			body: typeof body === 'object' ? JSON.stringify(body) : body,
			headers: {
				'Content-Type': 'application/json',
				...options.headers
			}
		});
	}

	async put(url, body, options = {}) {
		return this.request(url, {
			...options,
			method: 'PUT',
			body: typeof body === 'object' ? JSON.stringify(body) : body,
			headers: {
				'Content-Type': 'application/json',
				...options.headers
			}
		});
	}

	async patch(url, body, options = {}) {
		return this.request(url, {
			...options,
			method: 'PATCH',
			body: typeof body === 'object' ? JSON.stringify(body) : body,
			headers: {
				'Content-Type': 'application/json',
				...options.headers
			}
		});
	}

	async delete(url, options = {}) {
		return this.request(url, { ...options, method: 'DELETE' });
	}

	/**
	 * Configure the API client
	 */
	configure({ autoHandleAuth, redirectOn401, maxRetries } = {}) {
		if (autoHandleAuth !== undefined) {
			this.autoHandleAuth = autoHandleAuth;
		}
		if (redirectOn401 !== undefined) {
			this.redirectOn401 = redirectOn401;
		}
		if (maxRetries !== undefined) {
			this.maxRetries = maxRetries;
		}
	}
}

// Create singleton instance
const apiClient = new ApiClient();

// Export convenience functions as default
const api = {
	get: (url, options) => apiClient.get(url, options),
	post: (url, body, options) => apiClient.post(url, body, options),
	put: (url, body, options) => apiClient.put(url, body, options),
	patch: (url, body, options) => apiClient.patch(url, body, options),
	delete: (url, options) => apiClient.delete(url, options),
	request: (url, options) => apiClient.request(url, options),
	configure: (config) => apiClient.configure(config)
};

export default api;